<template>
  <MakerCartGuideLayout
    pageTitle="Conducting a Maker Activity"
    heroImage="/images/maker-cart-guide/frame-1000006494.png"
    previousUrl="/makercart/guide/8"
    nextUrl="/makercart/guide/10"
  >
    <div class="x-guide9-content">
      <!-- Intro Section -->
      <div class="x-intro-section">
        <h2 class="x-section-title">Role 3: Use appropriate language</h2>
        <p class="x-section-text">
          The language we use can significantly impact learner engagement and
          understanding. This section delves into the importance of clear,
          concise, and accessible language for maker educators. This is
          especially important when the maker educator notices a teachable
          moment, and respond with questions or prompts that promote deeper
          thought as well as socio-emotional learning (SEL) competencies.
        </p>
        <p class="x-section-text">
          To help illustrate how language use can invite rather than exclude,
          here are some language examples adapted from Youth Makerspace Playbook
          and other scenarios.
        </p>
      </div>

      <!-- Desktop Table Section -->
      <div v-if="!isMobile" class="x-table-container">
        <table class="x-scenario-table">
          <thead>
            <tr>
              <th class="x-table-header-left">Scenario</th>
              <th class="x-table-header-right">You could say...</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in scenarios" :key="index">
              <td>{{ item.scenario }}</td>
              <td>
                <div v-html="item.responses"></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Card Section -->
      <div v-if="isMobile" class="x-nav-card">
        <div class="x-card-header-bar">
          <div class="x-card-title-area">
            <!-- No title here as per Figma design for Page 9 -->
          </div>

          <div class="x-card-controls">
            <button
              class="x-control-btn"
              :class="{ 'x-btn-disabled': currentCardIndex === 0 }"
              @click="goToPreviousCard"
              :disabled="currentCardIndex === 0"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  :stroke="currentCardIndex === 0 ? '#4C4DDC' : '#FFFFFF'"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              class="x-control-btn"
              :class="{
                'x-btn-disabled': currentCardIndex === scenarios.length - 1,
              }"
              @click="goToNextCard"
              :disabled="currentCardIndex === scenarios.length - 1"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  :stroke="
                    currentCardIndex === scenarios.length - 1
                      ? '#4C4DDC'
                      : '#FFFFFF'
                  "
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="x-card-body">
          <div class="x-card-section">
            <h3 class="x-card-section-title">Scenario</h3>
            <p class="x-card-section-text">
              {{ scenarios[currentCardIndex].scenario }}
            </p>
          </div>

          <div class="x-card-divider"></div>

          <div class="x-card-section">
            <h3 class="x-card-section-title">You could say...</h3>
            <div
              class="x-card-section-text"
              v-html="scenarios[currentCardIndex].responses"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </MakerCartGuideLayout>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import MakerCartGuideLayout from "@/components/maker-cart/MakerCartGuideLayout.vue";

const isMobile = ref(false);
const currentCardIndex = ref(0);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});

const scenarios = [
  {
    scenario:
      "A student is frustrated with their project or stuck with a particular challenge.",
    responses: `
      <p>“It looks like you’re really struggling with this. What kind of help do you need? I wonder if someone else has encountered the same problem. Let’s check.”</p>
      <p>“I hear your frustration. I would be frustrated too after so much work. Would you like to take a break and come back to it? Perhaps you can help others with the battery connection you got working earlier?”</p>
      <p>“That doesn’t make any sense to me either. How cool is it that we have this mystery? I wonder what’s going on? When you figure it out, can you tell me so that I can share it with everyone? If not, we can all work together to figure it out.”</p>
    `,
  },
  {
    scenario:
      "You want to reinforce engagement, persistence, and a community of shared support.",
    responses: `
      <p>“Wow, you really stuck with this and worked through all of those problems. Would it be OK if I sent others to you for help with this kind of thing?”</p>
      <p>“Have you drawn any of these setups? I’d love to be able to share them with everyone.”</p>
      <p>“Can I take a picture of this to use as an example if someone gets stuck? What else do you think might be helpful to share?”</p>
      <p>“I notice you’ve worked incredibly hard on this and have still taken time to help others when they’ve been stuck. I wonder about where you got your inspirations and who might have helped you. Did any ideas come to you when you were helping with other projects?”</p>
    `,
  },
  {
    scenario:
      "A young maker is having a hard time getting started with open-ended explorations.",
    responses: `
      <p>“I hear that you’re having a hard time getting started and would prefer an example. I do have some if you’d like, but how about waiting a bit and seeing what others are trying out? Perhaps something will inspire you. Try just messing around first, just playing and having fun. I’ll check back with you in a bit to see how you’re feeling.”</p>
      <p>“I do have something that I’d like to show later as a possibility. Can I show it to you first and see what you make of it? I could use your help figuring out what kinds of things it might be useful for. I’d love to have you share it and your ideas, if you like.”</p>
    `,
  },
  {
    scenario: "You want to compliment and encourage the students",
    responses: `
      <p>Compliment the way that kids try different things (process) more than the results (outcome)</p>
      <p>“I admire how you worked through that hard problem. I noticed how you tried [x], [y], and [z] and you stuck with it until you figured it out.”</p>
      <p>“You must be proud of yourself for thinking about (small details).”</p>
      <p>"Look at that great sun you made, and you even included clouds!"</p>
      <p>"You worked really hard to cut straight lines, and I noticed you're starting to do it better"</p>
      <p>"I see you're working on your observation skills."</p>
    `,
  },
  {
    scenario: "Student asks you if their work is good enough.",
    responses: `
      <p>“What do you think?”</p>
      <p>“Do you like what you did?”</p>
      <p>“What would you do differently if you had more resources?”</p>
      <p>“What would you do if you were to do it again?”</p>
    `,
  },
];

const goToPreviousCard = () => {
  if (currentCardIndex.value > 0) {
    currentCardIndex.value--;
  }
};

const goToNextCard = () => {
  if (currentCardIndex.value < scenarios.length - 1) {
    currentCardIndex.value++;
  }
};
</script>

<style scoped>
.x-guide9-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.x-intro-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.x-section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
}

.x-section-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
  margin: 0;
  white-space: pre-wrap;
}

.x-table-container {
  width: 100%;
}

.x-desktop-only {
  display: block;
}

.x-mobile-only {
  display: none;
}

.x-scenario-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 16px;
  border: 1px solid #bac1cc;
}

.x-scenario-table thead tr:first-child th:first-child {
  border-top-left-radius: 15px;
}

.x-scenario-table thead tr:first-child th:last-child {
  border-top-right-radius: 15px;
}

.x-scenario-table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 15px;
}

.x-scenario-table tbody tr:last-child td:last-child {
  border-bottom-right-radius: 15px;
}

.x-scenario-table th {
  background-color: var(--primary-blue);
  color: #ffffff;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  text-align: center;
  padding: 20px 10px;
  border-bottom: 1px solid #bac1cc;
}

.x-scenario-table th.x-table-header-left {
  width: 50%;
  border-right: 1px solid #bac1cc;
}

.x-scenario-table th.x-table-header-right {
  width: 50%;
}

.x-scenario-table td {
  padding: 20px 10px;
  border-bottom: 1px solid #bac1cc;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
  vertical-align: middle;
}

.x-scenario-table tbody tr:last-child td {
  border-bottom: none;
}

.x-scenario-table td:first-child {
  font-weight: 700;
  border-right: 1px solid #bac1cc;
  white-space: pre-wrap;
}

.x-scenario-table td:last-child {
  font-weight: 400;
}

.x-scenario-table p {
  margin: 0 0 16px 0;
}

.x-scenario-table p:last-child {
  margin: 0;
}

/* Nav Card Styles for Mobile */
.x-nav-card {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #d8dae5;
  border-radius: 16px;
  overflow: hidden;
  padding: 16px;
}

.x-card-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
}

.x-card-title-area {
  flex: 1;
}

.x-card-controls {
  display: flex;
  gap: 4px;
  align-items: center;
}

.x-control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background-color: #4c4ddc;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.x-control-btn:hover:not(.x-btn-disabled) {
  opacity: 0.9;
}

.x-btn-disabled {
  background-color: transparent;
  border: 1px solid #4c4ddc;
  cursor: not-allowed;
}

.x-card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.x-card-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.x-card-section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-large);
  line-height: 28px;
  color: #00925e;
  margin: 0;
}

.x-card-section-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.035px;
  color: #3b4648;
}

.x-card-section-text p {
  margin: 0 0 16px 0;
}

.x-card-section-text p:last-child {
  margin: 0;
}

.x-card-divider {
  width: 100%;
  height: 1px;
  background-color: #d8dae5;
}

@media (max-width: 768px) {
  .x-desktop-only {
    display: none;
  }
  .x-mobile-only {
    display: block;
  }
}
</style>
